@if (label) {
    <label
        [id]="id + '_autocompleteLabel'"
        class="agpr-autocomplete-label"
        [for]="id"
    >
        {{ label | localize }}

        @if (required) {
            <span
                class="agpr-autocomplete-required"
                aria-hidden="true"
            >*</span>
        }

        @if (tooltipInfo) {
            <i
                class=" fa-regular fa-circle-info agpr-autocomplete-tooltipInfo"
                [agprTooltip]="tooltipInfo"
            ></i>
        }
    </label>
}

<div class="p-inputgroup">
    <!-- We need to add an ariaLabel wherever this component is used so that it meets accessibility standards. -->
    <prime-autoComplete
        #autocomplete
        [id]="`${id}_autocomplete`"
        [inputId]="`${id}_autocomplete_input`"
        [(ngModel)]="selectedItem"
        [suggestions]="filteredItems"
        [field]="labelField"
        [dropdown]="dropdown"
        [dropdownMode]="dropdownMode"
        [appendTo]="'body'"
        [baseZIndex]="2000"
        (completeMethod)="performAutocomplete($event.query)"
        (onSelect)="onItemSelect($event)"
        (onClear)="clearInput()"
        [placeholder]="placeholder | localize"
        [maxlength]="maxLength"
        [minLength]="minLength"
        [delay]="delay"
        [disabled]="disabled"
        dropdownIcon="fa-solid fa-angle-down"
        class="agpr-autocomplete"
        [autoHighlight]="autoHighlight"
        [forceSelection]="forceSelection"
        (onKeyUp)="handleOnKeyUp($event)"
        (onBlur)="onInputBlur($event)"
        (onFocus)="onInputFocus($event)"
        [styleClass]="!autocomplete.overlayVisible || !autocomplete?.highlightOption ? 'agpr-autocomplete-container--focused' : ''"
        [panelStyleClass]="panelStyleClass + ' agpr-clickOutsideExcluded agpr-autocomplete-panel'"
        [ngClass]="{
            'agpr-autocomplete-dropdown' : dropdown,
            'agpr-autocomplete-placeholder' : !selectedItem,
            'agpr-autocomplete-icon': icon,
            'agpr-autocomplete--socialPlus' : style === agprComponentStyle.socialPlus,
            'agpr-autocomplete-input--focused': !autocomplete.overlayVisible || !autocomplete?.highlightOption,
            'agpr-autocomplete--inputGroupLeft': inputGroupLeft,
            'agpr-autocomplete--flatBottomRight': flatBottomRight
        }"
        (onHide)="onHide($event)"
        (onShow)="onShow($event)"
        [ariaLabel]="ariaLabel | localize"
        [ariaLabelledBy]="ariaLabelledBy ? ariaLabelledBy : (label ? id + '_autocompleteLabel' : '')"
        [dropdownAriaLabel]="'Common.autocomplete.dropdownAriaLabel' | localize"
        [required]="required"
    >
        <ng-template let-item let-i="index" pTemplate="item">
            @let listItemId = `${id}_listItem_${i}`;
            <!-- this means it's a custom option -->
            @if (item.appearCondition !== undefined) {
                @if (item.labelHtml) {
                    <div
                        [id]="listItemId"
                        class="agpr-autocomplete-item"
                        [innerHTML]="item.labelHtml | localize: {query:item.searchString}"
                    ></div>
                }
                @if (item.label) {
                    <div
                        [id]="listItemId"
                        class="agpr-autocomplete-customOption agpr-autocomplete-item"
                        [ngClass]="{'agpr-autocomplete-item--selected': !multiSelection && selectedItemFromDropdown === item}"
                    >
                        {{ item.label | localize: {query:item.searchString} }}
                    </div>
                }
                @if (item.labelTemplate) {
                    <ng-container
                        [ngTemplateOutlet]="item.labelTemplate"
                        [ngTemplateOutletContext]="{
                            $implicit: item.labelTemplateVariable,
                            id: listItemId
                        }"
                    ></ng-container>
                }
                @if (item.showDivider) {
                    <div class="agpr-autocomplete-customOption-divider"></div>
                }
            }

            <!-- this means it's a "regular" option -->
            @if (item.appearCondition === undefined && !itemTemplate) {
                <div
                    [id]="listItemId"
                    class="agpr-autocomplete-item"
                    [ngClass]="{'agpr-autocomplete-item--selected': !multiSelection && selectedItemFromDropdown === item}"
                >
                    @if (labelField === undefined) {
                        <span>{{ item }}</span>
                    } @else {
                        <span>{{ item[labelField] }}</span>
                    }

                    @if (showHiddenOption && showHidden && (item.hidden || item.value?.hidden)) {
                        <span
                            class="agpr-autocomplete-item-hidden fa-regular fa-duotone fa-eye-slash"
                            role="img"
                            [agprTooltip]="'Common.forms.autocomplete.itemHidden'"
                        ></span>
                    }
                </div>
            }

            <!-- this means it will show user-defined label template -->
            @if (itemTemplate) {
                <ng-container
                    [ngTemplateOutlet]="itemTemplate"
                    [ngTemplateOutletContext]="{
                        $implicit: item,
                        id: listItemId
                    }"
                ></ng-container>
            }
        </ng-template>
    </prime-autoComplete>

    @if (icon) {
        <span class="p-inputgroup-addon">
            <i [class]="icon"></i>
        </span>
    }

    <div
        #ariaLiveRegion
        role="region"
        [id]="id + '_suggestionUpdate'"
        class="u-srOnly"
        aria-live="polite"
    ></div>
</div>

<ng-template #showHiddenItem let-item>
    <div
        class="agpr-autocomplete-customOption agpr-autocomplete-item"
        (click)="toggleHiddenItems($event)"
        tabindex="0"
    >
        {{ (showHidden ? 'Common.forms.autocomplete.hideHidden' : 'Common.forms.autocomplete.showHidden')
            | localize: {count: hiddenCount > 10 ? '10+' : hiddenCount} }}
    </div>
</ng-template>
